import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Copy, Download, Users, CalendarCheck, UserPlus, WifiOff, RefreshCw } from "lucide-react";
import { api, apiError, fmtDate } from "../lib/api";
import { pendingLeads, syncLeads, updateQueuedLead, removeQueuedLead } from "../lib/offlineQueue";
import { Button } from "../components/ui/button";
import { ManualLeadSheet } from "../components/ManualLeadSheet";
import { localeOf } from "../i18n";

export default function MyQr() {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const [qr, setQr] = useState(null);
  const [leads, setLeads] = useState([]);
  const [queued, setQueued] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);

  const load = () => {
    api.get("/me/qr").then(({ data }) => setQr(data)).catch((e) => toast.error(apiError(e)));
    api.get("/me/leads").then(({ data }) => setLeads(data)).catch(() => {});
    pendingLeads().then(setQueued).catch(() => {});
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const { synced } = await syncLeads();
      if (synced > 0) toast.success(t("qr.offlineSynced", { n: synced }));
      load();
    } finally { setSyncing(false); }
  };

  useEffect(() => {
    load();
    const onOnline = () => { runSync(); };
    window.addEventListener("online", onOnline);
    if (navigator.onLine) runSync();
    return () => window.removeEventListener("online", onOnline);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(qr.url);
      toast.success(t("common.linkCopied"));
    } catch {
      const el = document.createElement("textarea");
      el.value = qr.url;
      document.body.appendChild(el);
      el.select();
      try { document.execCommand("copy"); toast.success(t("common.linkCopied")); }
      catch { toast.error(qr.url); }
      document.body.removeChild(el);
    }
  };

  if (!qr) return <p className="text-sm text-muted-foreground">{t("common.loading")}</p>;

  return (
    <div className="space-y-5" data-testid="my-qr-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold">{t("qr.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("qr.lead")}</p>
      </div>

      <div className="vy-card p-5 text-center" data-testid="my-qr-card">
        <img src={qr.qr_png} alt={t("qr.title")} className="mx-auto h-56 w-56 rounded-2xl bg-white p-2"
          data-testid="my-qr-image" />
        <p className="mt-3 break-all text-xs text-muted-foreground" data-testid="my-qr-url">{qr.url}</p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <Button data-testid="qr-copy-btn" variant="outline" onClick={copy}
            className="h-12 rounded-full font-bold">
            <Copy className="mr-2 h-4 w-4" />{t("common.copyLink")}
          </Button>
          <a href={qr.qr_png} download="mon-qr-vytalidays.png" data-testid="qr-download-btn"
            className="inline-flex h-12 items-center rounded-full bg-primary px-5 text-sm font-bold text-primary-foreground">
            <Download className="mr-2 h-4 w-4" />{t("qr.download")}
          </a>
        </div>
      </div>

      <div className="vy-card p-5" data-testid="qr-context-card">
        <div className="flex items-center gap-2 text-muted-foreground">
          <CalendarCheck className="h-4 w-4" />
          <span className="text-xs font-bold uppercase tracking-wide">{t("qr.attribution")}</span>
        </div>
        {qr.active_event ? (
          <div className="mt-2" data-testid="qr-active-event">
            <p className="font-display text-base font-bold">{qr.active_event.event_title}</p>
            <p className="text-xs text-muted-foreground">
              {fmtDate(qr.active_event.starts_at, locale)}
              {qr.active_event.partner_name ? ` · ${qr.active_event.partner_name}` : ""}
              {qr.active_event.campaign_name ? ` · ${qr.active_event.campaign_name}` : ""}
            </p>
            <p className="mt-2 text-xs font-semibold text-[hsl(var(--success))]">{t("qr.autoAttributed")}</p>
          </div>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground" data-testid="qr-no-active-event">
            {t("qr.noActiveEvent")}
          </p>
        )}
      </div>

      <div className="vy-card p-5" data-testid="manual-lead-card">
        <div className="flex items-center gap-2 text-muted-foreground">
          <UserPlus className="h-4 w-4" />
          <span className="text-xs font-bold uppercase tracking-wide">{t("qr.manualTitle")}</span>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">{t("qr.manualHint")}</p>
        <Button data-testid="manual-lead-open-btn" onClick={() => setManualOpen(true)}
          className="mt-3 h-12 w-full rounded-full font-bold">
          <UserPlus className="mr-2 h-4 w-4" />{t("qr.manualCta")}
        </Button>
      </div>

      {queued.length > 0 && (
        <div className="vy-card p-5" data-testid="offline-leads-card">
          <div className="flex items-center gap-2">
            <WifiOff className="h-4 w-4 text-[hsl(var(--warning))]" />
            <h2 className="font-display text-base font-bold">{t("qr.offlinePending")}</h2>
            <span className="ml-auto font-display text-xl font-extrabold"
              data-testid="offline-leads-count">{queued.length}</span>
          </div>
          <ul className="mt-3 divide-y divide-border">
            {queued.map((q) => (
              <li key={q.client_submission_id} className="space-y-2 py-3"
                data-testid={`offline-lead-${q.client_submission_id}`}>
                <p className="text-sm font-semibold">
                  {q.first_name} {q.last_name || ""}
                  {q.business_name ? ` · ${q.business_name}` : ""}
                </p>
                <p className="text-xs font-semibold text-[hsl(var(--warning))]">
                  {t("qr.offlineWaiting")} · {fmtDate(q.captured_at, locale)}
                </p>
                {q.needs_event && (
                  <div>
                    <p className="text-xs text-muted-foreground">{t("qr.offlineNeedsEvent")}</p>
                    <select data-testid={`offline-lead-event-${q.client_submission_id}`}
                      className="mt-1 h-11 w-full rounded-md border border-input bg-background px-3 text-sm font-semibold"
                      value={q.event_id || ""}
                      onChange={async (e) => {
                        const v = e.target.value;
                        await updateQueuedLead(q.client_submission_id,
                          { event_id: v || null, needs_event: !v });
                        setQueued(await pendingLeads());
                      }}>
                      <option value="">{t("capture.noEvent")}</option>
                      {(qr.selectable_events || []).map((ev) => (
                        <option key={ev.id} value={ev.id}>{ev.title}</option>
                      ))}
                    </select>
                    <Button variant="outline" className="mt-2 h-10 w-full rounded-full text-xs font-bold"
                      data-testid={`offline-lead-sync-no-event-${q.client_submission_id}`}
                      onClick={async () => {
                        await updateQueuedLead(q.client_submission_id,
                          { event_id: null, needs_event: false });
                        setQueued(await pendingLeads());
                        await runSync();
                      }}>
                      {t("qr.offlineSyncNoEvent")}
                    </Button>
                    <Button variant="ghost" className="mt-1 h-9 rounded-full text-xs"
                      data-testid={`offline-lead-discard-${q.client_submission_id}`}
                      onClick={async () => {
                        await removeQueuedLead(q.client_submission_id);
                        setQueued(await pendingLeads());
                      }}>
                      {t("common.delete")}
                    </Button>
                  </div>
                )}
                {q.last_error && q.last_error !== "event" && (
                  <p className="text-xs font-semibold text-destructive"
                    data-testid={`offline-lead-error-${q.client_submission_id}`}>
                    {t("qr.offlineError", { msg: q.last_error })}
                  </p>
                )}
              </li>
            ))}
          </ul>
          <Button data-testid="offline-leads-sync-btn" variant="outline" disabled={syncing}
            onClick={runSync} className="mt-3 h-12 w-full rounded-full font-bold">
            <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
            {t("qr.offlineSyncNow")}
          </Button>
        </div>
      )}

      <div className="vy-card p-5" data-testid="qr-leads-card">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-display text-lg font-bold">{t("qr.myLeads")}</h2>
          <span className="ml-auto font-display text-2xl font-extrabold" data-testid="qr-leads-count">
            {qr.leads_captured}
          </span>
        </div>
        <ul className="mt-3 divide-y divide-border">
          {leads.map((l) => (
            <li key={l.id} className="py-3" data-testid={`qr-lead-${l.id}`}>
              <p className="text-sm font-semibold">
                {l.first_name} {l.last_name || ""}{l.business_name ? ` · ${l.business_name}` : ""}
              </p>
              <p className="text-xs text-muted-foreground">
                {fmtDate(l.created_at, locale)}{l.event_title ? ` · ${l.event_title}` : ""}
              </p>
            </li>
          ))}
          {leads.length === 0 && (
            <li className="py-3 text-sm text-muted-foreground">{t("qr.noLeads")}</li>
          )}
        </ul>
      </div>

      <ManualLeadSheet open={manualOpen} onClose={() => setManualOpen(false)}
        onDone={() => { setManualOpen(false); load(); }}
        activeEvent={qr.active_event} selectableEvents={qr.selectable_events || []} />
    </div>
  );
}
